import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import useHttp from "../../hooks/use-http";
import { useAppDispatch } from "../../store/hooks";
import { AuthActions } from "../../store/auth-slice";
import { UIActions } from "../../store/ui-slice";

import FormLine from "../UI/FormLine";
import Input from "../UI/Input";
import RoundedButton from "../UI/RoundedButton";

import classes from "./LoginForm.module.css";
import { lang, t } from "../../i18n";

const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

type LoginFormValues = { email: string; password: string };
type ForgetFormValues = { email: string };

const loginSchema: yup.ObjectSchema<LoginFormValues> = yup.object({
  email: yup.string().trim().matches(emailRegex).required(),
  password: yup.string().trim().min(7).required(),
});

const forgetSchema: yup.ObjectSchema<ForgetFormValues> = yup.object({
  email: yup.string().trim().matches(emailRegex).required(),
});

const REMEMBERED_EMAIL_KEY = "rememberedEmail";

const LoginForm = () => {
  const dispatch = useAppDispatch();
  const [doesForgetPassword, setDoesForgetPassword] = useState(false);
  const { sendRequest: sendAuthRequest } = useHttp();

  const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? "";

  const loginForm = useForm<LoginFormValues>({
    resolver: yupResolver(loginSchema),
    mode: "onBlur",
    defaultValues: { email: rememberedEmail, password: "" },
  });

  const forgetForm = useForm<ForgetFormValues>({
    resolver: yupResolver(forgetSchema),
    mode: "onBlur",
    defaultValues: { email: rememberedEmail },
  });

  const switchFormToForgetHandler = () => {
    forgetForm.setValue("email", loginForm.getValues("email"));
    setDoesForgetPassword(true);
  };

  const switchFormToLoginHandler = () => {
    loginForm.setValue("email", forgetForm.getValues("email"));
    setDoesForgetPassword(false);
  };

  const afterLogin = (data: {
    kind: string;
    localId: string;
    email: string;
    displayName: string;
    idToken: string;
    registered: boolean;
    refreshToken: string;
    expiresIn: string;
  }) => {
    localStorage.setItem(REMEMBERED_EMAIL_KEY, data.email);
    dispatch(
      AuthActions.login({
        token: data.idToken,
        expirationTime: data.expiresIn,
        localId: data.localId,
        refreshToken: data.refreshToken,
      })
    );
  };

  const afterForget = () => {
    setDoesForgetPassword(false);
    dispatch(
      UIActions.success({
        message: t.resetLinkSent,
      })
    );
  };

  const invalidLoginHandler = () => {
    dispatch(
      UIActions.error({
        message: t.enterCredentials,
      })
    );
  };

  const invalidForgetHandler = () => {
    dispatch(
      UIActions.error({
        message: t.enterValidEmail,
      })
    );
  };

  const loginHandler = (values: LoginFormValues) => {
    sendAuthRequest(
      {
        url:
          "https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=" +
          window.firebaseKey,
        method: "POST",
        body: {
          email: values.email,
          password: values.password,
          returnSecureToken: true,
        },
        headers: {
          "Content-Type": "application/json",
        },
        hideAfterRequest: true,
      },
      afterLogin
    );
  };

  const forgetHandler = (values: ForgetFormValues) => {
    sendAuthRequest(
      {
        url:
          "https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=" +
          window.firebaseKey,
        method: "POST",
        body: {
          email: values.email,
          requestType: "PASSWORD_RESET",
        },
        headers: {
          "Content-Type": "application/json",
          "X-Firebase-Locale": lang,
        },
        hideAfterRequest: false,
      },
      afterForget
    );
  };

  let buttonLabel = t.logIn;
  if (doesForgetPassword) {
    buttonLabel = t.sendResetLink;
  }

  return (
    <form
      autoComplete="on"
      noValidate
      onSubmit={
        !doesForgetPassword
          ? loginForm.handleSubmit(loginHandler, invalidLoginHandler)
          : forgetForm.handleSubmit(forgetHandler, invalidForgetHandler)
      }
    >
      <FormLine>
        {!doesForgetPassword ? (
          <Input
            type="email"
            inputMode="email"
            classes="separate"
            maxLength={316}
            autoComplete="email"
            placeholder={t.email}
            error={!!loginForm.formState.errors.email}
            {...loginForm.register("email")}
          />
        ) : (
          <Input
            type="email"
            inputMode="email"
            classes="separate"
            maxLength={316}
            autoComplete="email"
            placeholder={t.email}
            error={!!forgetForm.formState.errors.email}
            {...forgetForm.register("email")}
          />
        )}
      </FormLine>
      {!doesForgetPassword && (
        <FormLine>
          <Input
            type="password"
            classes="separate"
            maxLength={64}
            autoComplete="current-password"
            placeholder={t.password}
            error={!!loginForm.formState.errors.password}
            {...loginForm.register("password")}
          />
        </FormLine>
      )}

      {!doesForgetPassword && (
        <div className={classes["forget-wrapper"]}>
          <button type="button" onClick={switchFormToForgetHandler} className={classes.link}>
            {t.forgotPassword}
          </button>
        </div>
      )}

      <RoundedButton label={buttonLabel} />

      {doesForgetPassword && (
        <div className={classes["enter-wrapper"]}>
          <button type="button" onClick={switchFormToLoginHandler} className={classes.link}>
            &larr; {t.backToLogIn}
          </button>
        </div>
      )}
    </form>
  );
};

export default LoginForm;
